import { Row, Col, message, Alert, Form, DatePicker, Select } from "antd";
import Table, { ColumnsType } from "antd/lib/table";
import { AUTH_HEADER_NAME, apis, } from "common/apis";
import { IPickerMappingUpload, IPickerMappingUploadRow } from "common/interface/IPickerUploadScheme";
import { routes } from "common/routes";
import { generateHeaders } from "common/utils";
import { AppPageHeader } from "components/AppPageHeader";
import { AppUpload } from "components/AppUpload";
import { useAppDispatch } from "hooks/useAppDispatch";
import { useAppSelector } from "hooks/useAppSelector";
import { useDocumentTitle } from "hooks/usePageTitle";
import moment from "moment";
import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { getWarehouseId } from "services/PersistService/PersistWareHouseReturn";
import { fetchStoresList, removeProcessingStoreReturnMapping, setProcessingStoreReturnMapping } from "store/store-return/storeReturnUploadSlice";

const StoreReturnUploadPage = () => {
  const { authToken } = useAppSelector((state) => state.app.user);
  const { processingStoreReturn, storesList } = useAppSelector(
    (state) => state.storeReturnUpload
  );
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const [hasErrorRows, setHasErrorRows] = useState(false);
  const [storeId, setStoreId] = useState<string>();
  const [returnDate, setDate] = useState<string>();

  useEffect(() => {
    if (
      processingStoreReturn &&
      processingStoreReturn.key
    ) {
      navigate(`/${routes.STORE_RETURN.SAVE}`, {});
    }

    return () => {
      message.destroy();
    };
  }, [processingStoreReturn, navigate]);

  useEffect(() => {
    dispatch(fetchStoresList(""));
  }, []);

  useEffect(() => {
    const failed = processingStoreReturn?.failedRows?.length;

    if (failed) {
      setHasErrorRows(true);
    }
  }, [processingStoreReturn?.failedRows]);

  const headers: { [name: string]: string } = {
    ...(authToken && { [AUTH_HEADER_NAME]: authToken }),
  };

  const onUploaded = (data: IPickerMappingUpload) => {
    dispatch(setProcessingStoreReturnMapping(data));
  };

  const onUploading = () => {
    dispatch(removeProcessingStoreReturnMapping());
  };

  // useEffect(() => {
  //   return (() => {
  //     dispatch(removeProcessingStoreReturnMapping())
  //   });
  // }, []);

  const { headerMapping } = processingStoreReturn;
  const headerMappingWithError = headerMapping + ",errors:Errors";

  const generateMemoizedHeaders = useMemo(
    () => generateHeaders(headerMappingWithError),
    [headerMappingWithError]
  );

  const tableHeaders = generateMemoizedHeaders || {};
  const successRows = processingStoreReturn?.successRows || [];
  const failedRows = processingStoreReturn?.failedRows || [];
  const successData = successRows?.map((row, i) => ({
    ...row,
    key: `data-success-${i}`,
  }));
  const failedData = failedRows?.map((row, i) => ({
    ...row,
    key: `data-failed-${i}`,
  }));
  const data = [...failedData, ...successData];
  const columns: ColumnsType<
    IPickerMappingUploadRow & { key: string }
  > = [
      ...Object.keys(tableHeaders).map((key) => ({
        key,
        title: tableHeaders[key],
        dataIndex: key,
        render: (
          text: string,
          record: IPickerMappingUploadRow & { key: string }
        ) => {
          if (key === "errors") {
            const errorMessages = record.errors
              ?.map((error) => error.message)
              .join(", ");
            return <>{errorMessages}</>;
          }
          return <>{text}</>;
        },
      })),
    ];

  const addBorderToErrorRows = (
    record: IPickerMappingUploadRow & { key: string }
  ) => {
    if (record.errors && record.errors.length) {
      return "error-row";
    }

    return "";
  };

  useDocumentTitle("Store Return Upload");
  return (
    <>
      <Row>
        <Col>
          <AppPageHeader title="Upload Store Return Mapping" />
        </Col>
      </Row>
      <>
        <Form>
          <Form.Item label="Return date" name="date" required>
            <DatePicker
              disabledDate={(current) => {

                return current && current > moment(moment(), "YYYY-MM-DD");
              }}
              onChange={(val) => {
                setDate(val?.format("yyyy-MM-DD"));
              }}
            />
          </Form.Item>
          <Form.Item label="Store ID" name="storeId" required>

            <Select
              showSearch
              filterOption={(input, option) => {                
                return option?.children?.toString().toLowerCase().includes(input?.toLowerCase() ?? "") ?? false;
              }}
              style={{ width: "200px" }}
              onChange={(e) => {
                setStoreId(e);
              }}
            
            >
              {(storesList ?? []).map((storeInfo) => (
                <Select.Option value={storeInfo.id} key={storeInfo.id}>
                  {storeInfo.name}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>
        </Form>
      </>
      {(storeId && returnDate &&
        <Row>
          <Col lg={16} xs={24}>
            <AppUpload
              uploadUrl={apis.uploadStoreReturnMapping}
              headers={headers}
              onUploaded={onUploaded}
              onUploading={onUploading}
              hasErrorRows={hasErrorRows}
              data={{ storeId, returnDate, }}
            />
          </Col>
        </Row>
      )}
      {Object.keys(processingStoreReturn).length != 0 &&
        !processingStoreReturn.key && (
          <>
            <Row>
              <Col lg={16} xs={24}>
                <Alert
                  type="error"
                  message="Errors in sheet data."
                  description="Please correct errors and upload again."
                  showIcon
                  banner
                />
              </Col>
            </Row>

            <Row>
              <Table
                size="small"
                columns={columns}
                dataSource={data}
                rowClassName={(record) => addBorderToErrorRows(record)}
                scroll={{ y: 500 }}
                className="table-with-errors"
              ></Table>
            </Row>
          </>
        )}
    </>
  );
};

export default StoreReturnUploadPage;